import React, { FC, useState } from "react";
import { Link } from "react-router-dom";

import { toast } from "react-toastify";

import {
  useApisAdminOperatorsCompaniesIndex,
  useApisAdminOperatorsDbBackupsCreate,
  useDropdown,
  useInput,
} from "~/hooks";

import { AdminOperatorLayout } from "~/components/layouts";

import { ApisAdminOperatorsCompaniesIndexRequestType } from "~/domains";

import {
  Button,
  Form,
  FormSubmitButton,
  GridHeaderCells,
  LinkButton,
  LabelWithDropDownField,
  LabelWithTextField,
} from "~/ui";
import { GridRow, InfiniteScrollWithMoreButton } from "~/ui";

export const AdminOperatorsCompaniesIndex: FC = () => {
  const breadCrumbItems = [
    {
      label: "運営者画面",
      href: "/admin_operators",
    },
    {
      label: "会社一覧",
      href: "/admin_operators",
    },
  ];

  const [{ value: name, onChange: onChangeName }] = useInput("");
  const [apiSearchParams, setApiSearchParams] =
    useState<ApisAdminOperatorsCompaniesIndexRequestType>({
      name: "",
      contractStatus: true,
    });

  const [
    {
      value: contractStatus,
      dropdownValue: dropdownValueContractStatus,
      onChange: onChangeContractStatus,
      dropdownSelectableValue: contractStatusSelectableValue,
    },
  ] = useDropdown({
    selectableValue: [
      { id: "", name: "すべて" },
      { id: "true", name: "契約中" },
      { id: "false", name: "未契約" },
    ],
    initialValue: { id: "active", name: "契約中" },
  });

  const { data, fetchNextPage, hasNextPage } =
    useApisAdminOperatorsCompaniesIndex({ params: apiSearchParams });

  const { mutate, isLoading: isSubmitting } =
    useApisAdminOperatorsDbBackupsCreate();

  const handleDbBackup = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const isConfirm = confirm("DBバックアップを実行しますか？");
    if (!isConfirm) return;

    mutate(
      {},
      {
        onSuccess: (data) => {
          toast.success(data.message);
        },
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let parsedContractStatus: boolean | undefined;
    if (contractStatus?.id === "true") parsedContractStatus = true;
    else if (contractStatus?.id === "false") parsedContractStatus = false;

    setApiSearchParams({
      name: name.trim(),
      contractStatus: parsedContractStatus,
    });
  };

  return (
    <AdminOperatorLayout breadCrumbItems={breadCrumbItems}>
      <div className="flex items-center justify-between">
        <h1 className="text-xl">会社一覧</h1>
        <div className="flex space-x-4">
          <Button
            onClick={handleDbBackup}
            text="DBバックアップ"
            color="primary"
            outline
            readonly={isSubmitting}
          />
          <LinkButton
            path="/admin_operators/companies/new"
            text="会社を新規作成する"
            color="primary"
          />
        </div>
      </div>
      <Form
        onSubmit={handleSearch}
        className="mt-4 p-4 bg-secondary-200 rounded"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <LabelWithTextField
            labelText="会社名"
            type="text"
            name="name"
            value={name}
            placeholder="会社名で検索"
            onChange={onChangeName}
          />
          <LabelWithDropDownField
            labelText="契約状況"
            name="contractStatus"
            value={dropdownValueContractStatus}
            onChange={onChangeContractStatus}
            options={contractStatusSelectableValue}
          />
          <div className="flex items-end">
            <FormSubmitButton value="検索" color="primary" />
          </div>
        </div>
      </Form>
      <InfiniteScrollWithMoreButton
        itemsLength={data?.pages.length}
        nextFetchFunction={fetchNextPage}
        hasMore={hasNextPage}
      >
        <div className="mt-4 grid grid-cols-5">
          <GridHeaderCells
            texts={["名前", "サブドメイン", "作成日時", "更新日時", "契約状況"]}
          />
          {data?.pages.map((page) =>
            page.companies.map((company) => (
              <Link
                to={`/admin_operators/companies/${company.id}`}
                key={company.id}
                className={`col-span-5 grid grid-cols-5 hover:bg-secondary-300 ${
                  company.active ? "" : "bg-red-200"
                }`}
              >
                <GridRow
                  lists={[
                    company.name,
                    company.subdomain,
                    company.createdAt,
                    company.updatedAt,
                    company.active ? "契約中" : "未契約",
                  ]}
                />
              </Link>
            )),
          )}
        </div>
      </InfiniteScrollWithMoreButton>
    </AdminOperatorLayout>
  );
};
